import { ResponseArray } from '@/types/shared'
import datacentral from '@/utils/axios/datacentral'
import qs from 'query-string'
import {
    EmployeeReportFilters,
    EmployeeReportItem,
    MonthlyExportFilters,
    MonthlyExportItem,
    ProductGroupReportFilters,
    ProductGroupReportItem,
    ServiceMonthlyReportFilters,
    ServiceMonthlyReportItem,
} from './types'

const BASE_URL = '/reports/bi/services'

/**
 * Fetches monthly service report data
 * GET /api/reports/bi/services/reports/monthly
 * @param filters - year (required), product_group_name[], account_manager_codes[], sale_manager_codes[] (optional)
 * @returns Array of monthly report items
 */
export const getServiceMonthlyReport = async (
    filters: ServiceMonthlyReportFilters
) => {
    const response = await datacentral.get<
        ResponseArray<ServiceMonthlyReportItem>
    >(
        `${BASE_URL}/reports/monthly?${qs.stringify(filters, {
            arrayFormat: 'bracket',
        })}`
    )
    return response.data.results || []
}

/**
 * Fetches product group report data
 * GET /api/reports/bi/services/reports/product-group
 * @param filters - from_date, to_date (required), account_manager_codes[], sale_manager_codes[] (optional)
 * @returns Array of product group report items
 */
export const getProductGroupReport = async (
    filters: ProductGroupReportFilters
) => {
    const response = await datacentral.get<
        ResponseArray<ProductGroupReportItem>
    >(
        `${BASE_URL}/reports/product-group?${qs.stringify(filters, {
            arrayFormat: 'bracket',
        })}`
    )
    return response.data.results || []
}

/**
 * Fetches employee report data
 * GET /api/reports/bi/services/reports/employee
 * @param filters - from_date, to_date, employee_type (required), account_manager_codes[], sale_manager_codes[] (optional)
 * @returns Array of employee report items
 */
export const getEmployeeReport = async (filters: EmployeeReportFilters) => {
    const response = await datacentral.get<ResponseArray<EmployeeReportItem>>(
        `${BASE_URL}/reports/employee?${qs.stringify(filters, {
            arrayFormat: 'bracket',
        })}`
    )
    return response.data.results || []
}

/**
 * Utility function to calculate growth rate
 * @param current - Current value
 * @param previous - Previous value
 * @returns Growth rate as percentage or null if previous is 0
 */
export const calculateGrowthRate = (
    current: number,
    previous: number | undefined
): number | null => {
    if (previous === undefined || previous === 0) return null
    return ((current - previous) / previous) * 100
}

/**
 * Utility function to calculate change (for rates/percentages)
 * @param current - Current value
 * @param previous - Previous value
 * @returns Absolute change or null
 */
export const calculateChange = (
    current: number,
    previous: number | undefined
): number | null => {
    if (previous === undefined) return null
    return current - previous
}

/**
 * Fetches monthly service report export data with detailed service info
 * GET /api/reports/bi/services/reports/monthly/export
 * @param filters - year (required), product_group_name[], account_manager_codes[], sale_manager_codes[] (optional)
 * @returns Array of monthly export items with detailed service data
 */
export const getServiceMonthlyReportExport = async (
    filters: MonthlyExportFilters
) => {
    const response = await datacentral.get<ResponseArray<MonthlyExportItem>>(
        `${BASE_URL}/reports/monthly/export?${qs.stringify(filters, {
            arrayFormat: 'bracket',
        })}`
    )
    return response.data.results || []
}
